import { capitalize } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveHardwareName } from "src/data/products";
import { getSettings } from "src/services/configService";
import { filesize } from "src/ui/utils/filesize";
import { getSafeExternalUrl } from "src/ui/utils/validityChecker";
import { makeStyles } from "tss-react/mui";

import { getLatestVersionReleaseNotesChanges } from "@dashboard/shared/utils/versionReleaseNotes";
import { Box, Collapse, IconButton, Theme, Typography } from "@mui/material";
import TimeService from "@services/timeService/timeService";
import { PrimaryButton } from "@shared/CustomButton";
import { Modal } from "@shared/Modal/Modal";
import { TextLink } from "@shared/TextLink";

const useStyles = makeStyles()((theme: Theme) => ({
    content: {
        display: "flex",
        flexDirection: "column",
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    description: {
        whiteSpace: "pre-wrap",
    },
    metaRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    metaValue: {
        textAlign: "right",
    },
    collapsibleHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        cursor: "pointer",
        userSelect: "none",
    },
    chevronButton: {
        padding: theme.spacing(0.5),
        color: theme.palette.emteria.text.secondary,
    },
    collapsibleLabelOpen: {
        color: theme.palette.emteria.link,
        "&:hover": {
            color: theme.palette.emteria.warning,
        },
    },
    releaseNotesBox: {
        maxHeight: 140,
        overflowY: "auto",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(2),
    },
    releaseNotesList: {
        margin: 0,
        paddingLeft: theme.spacing(3),
        listStylePosition: "outside",
    },
    releaseNotesItem: {
        display: "list-item",
        margin: 0,
        "&:not(:last-child)": {
            marginBottom: theme.spacing(1),
        },
    },
    externalLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
    },
}));

const ExternalLinkIcon = () => (
    <Box
        component="i"
        className="fa-solid fa-external-link"
        aria-hidden
        sx={{ fontSize: (theme) => theme.typography.body2.fontSize }}
    />
);

interface MetaRowProps {
    label: string;
    value: React.ReactNode;
    emphasized?: boolean;
}

const MetaRow = ({ label, value, emphasized = false }: MetaRowProps) => {
    const { classes } = useStyles();
    return (
        <Box className={classes.metaRow}>
            <Typography variant="body1" color="text.secondary">
                {label}
            </Typography>
            {typeof value === "string" ? (
                <Typography variant="body1" fontWeight={emphasized ? "bold" : "regular"} className={classes.metaValue}>
                    {value}
                </Typography>
            ) : (
                <Box className={classes.metaValue}>{value}</Box>
            )}
        </Box>
    );
};

interface CollapsibleBlockProps {
    label: string;
    open: boolean;
    onToggle: () => void;
    tooltip: string;
    children: React.ReactNode;
}

const CollapsibleBlock = ({ label, open, onToggle, tooltip, children }: CollapsibleBlockProps) => {
    const { classes } = useStyles();
    return (
        <Box>
            <Box className={classes.collapsibleHeader} onClick={onToggle}>
                {!open ? (
                    <TextLink tooltip={tooltip} variant="body1">
                        {label}
                    </TextLink>
                ) : (
                    <Typography variant="body1" className={classes.collapsibleLabelOpen}>
                        {label}
                    </Typography>
                )}
                <IconButton className={classes.chevronButton} size="small" disableRipple tabIndex={-1}>
                    <i className={`fa-solid ${open ? "fa-chevron-up" : "fa-chevron-down"}`} />
                </IconButton>
            </Box>
            <Collapse in={open}>{children}</Collapse>
        </Box>
    );
};

interface Props {
    file: API.ReleaseFile;
    open: boolean;
    onClose: () => void;
    onDownload: () => Promise<boolean>;
    loading?: boolean;
}

export const DownloadImageModal = ({ file, open, onClose, onDownload, loading = false }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common", "modals"]);
    const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setReleaseNotesOpen(false);
            setHelpOpen(false);
        }
    }, [open]);

    const forumUrl = getSettings().features.helpLinks?.discussionForum;
    const organizationUrl = getSafeExternalUrl(file.organizationUrl);
    const documentationUrl = getSafeExternalUrl(file.documentationUrl);
    const organizationName = file.organizationName?.trim() || null;
    const description = file.description?.trim() || null;
    const hardwareName = useMemo(() => resolveHardwareName(file.codename), [file.codename]);
    const releaseNotes = useMemo(
        () => getLatestVersionReleaseNotesChanges(file.changesWithDate, file.version),
        [file.changesWithDate, file.version],
    );
    const channelLabel = capitalize(file.channel ?? "none");
    const hasHelpResources = Boolean(forumUrl || documentationUrl);

    const handleDownload = async () => {
        const succeeded = await onDownload();
        if (succeeded) {
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={file.productName}
            secondaryActionText={t("common:button.cancel")}
            endButton={
                <PrimaryButton onClick={handleDownload} loading={loading} disabled={loading} name="confirm-download-file">
                    {t("common:button.download")}
                </PrimaryButton>
            }
        >
            <Box className={classes.content}>
                {description ? (
                    <Box className={classes.header}>
                        <Typography variant="body1" className={classes.description}>
                            {description}
                        </Typography>
                    </Box>
                ) : null}

                <MetaRow label={t("common:label.version")} value={file.version} emphasized />
                <MetaRow label={t("common:label.hardware")} value={hardwareName} emphasized />
                <MetaRow label={t("common:label.size")} value={filesize(file.size)} />
                <MetaRow label={t("common:label.channel")} value={channelLabel} />
                {organizationName ? (
                    <MetaRow
                        label={t("common:label.publishedBy")}
                        value={
                            organizationUrl ? (
                                <TextLink
                                    href={organizationUrl}
                                    target="_blank"
                                    variant="body1"
                                    tooltip={t("modals:downloadImageModal.tooltip.openPublisherWebsite")}
                                    className={classes.externalLink}
                                >
                                    {organizationName}
                                    <ExternalLinkIcon />
                                </TextLink>
                            ) : (
                                organizationName
                            )
                        }
                    />
                ) : null}
                <MetaRow
                    label={t("common:label.creationDate")}
                    value={file.created ? TimeService.getLocalDate(file.created, "ISO", "date") : "-"}
                />

                <CollapsibleBlock
                    label={t("modals:downloadImageModal.labels.releaseNotes")}
                    tooltip={t("modals:downloadImageModal.tooltip.showReleaseNotes")}
                    open={releaseNotesOpen}
                    onToggle={() => setReleaseNotesOpen((prev) => !prev)}
                >
                    <Box className={classes.releaseNotesBox}>
                        {releaseNotes.length > 0 ? (
                            <ul className={classes.releaseNotesList}>
                                {releaseNotes.map((change, index) => (
                                    <Typography key={index} component="li" variant="body1" className={classes.releaseNotesItem}>
                                        {change}
                                    </Typography>
                                ))}
                            </ul>
                        ) : (
                            <Typography variant="body1" color="text.secondary">
                                {t("modals:changesDialog.noChanges")}
                            </Typography>
                        )}
                    </Box>
                </CollapsibleBlock>

                {hasHelpResources ? (
                    <CollapsibleBlock
                        label={t("modals:downloadImageModal.labels.needHelp")}
                        tooltip={t("modals:downloadImageModal.tooltip.showHelp")}
                        open={helpOpen}
                        onToggle={() => setHelpOpen((prev) => !prev)}
                    >
                        <Box className={classes.releaseNotesBox}>
                            <ul className={classes.releaseNotesList}>
                                {documentationUrl ? (
                                    <li className={classes.releaseNotesItem}>
                                        <TextLink
                                            variant="body1"
                                            href={documentationUrl}
                                            target="_blank"
                                            tooltip={t("modals:downloadImageModal.tooltip.openKnowledgeBase")}
                                            className={classes.externalLink}
                                        >
                                            {t("modals:downloadImageModal.labels.productDocumentation")}
                                            <ExternalLinkIcon />
                                        </TextLink>
                                    </li>
                                ) : null}
                                {forumUrl ? (
                                    <li className={classes.releaseNotesItem}>
                                        <TextLink
                                            variant="body1"
                                            href={forumUrl}
                                            target="_blank"
                                            tooltip={t("modals:downloadImageModal.tooltip.openDiscussionForum")}
                                            className={classes.externalLink}
                                        >
                                            {t("modals:downloadImageModal.labels.emteriaDiscussionForum")}
                                            <ExternalLinkIcon />
                                        </TextLink>
                                    </li>
                                ) : null}
                            </ul>
                        </Box>
                    </CollapsibleBlock>
                ) : null}
            </Box>
        </Modal>
    );
};
